import {
  Box,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  SelectChangeEvent
} from "@mui/material";
import { useState, useMemo } from "react";
import WorkingScaleCaption from "./WorkingScaleCaption";
import { sharedStyles } from "@/styles/shared";
import {
  validAvailability,
  invalidAvailability,
  validAvailabilityBorder,
  invalidAvailabilityBorder,
} from "@/styles/colors";
import { generateTimeSlots } from "@/pages/petAndBeauty/Comercial/workingTimeConstants";
import { useTranslation } from "react-i18next";

interface WorkingScaleSelectorProps {
  timeMonday: string[];
  setTimeMonday: (time: string[]) => void;
  timeTuesday: string[];
  setTimeTuesday: (time: string[]) => void;
  timeWednesday: string[];
  setTimeWednesday: (time: string[]) => void;
  timeThursday: string[];
  setTimeThursday: (time: string[]) => void;
  timeFriday: string[];
  setTimeFriday: (time: string[]) => void;
  timeSaturday: string[];
  setTimeSaturday: (time: string[]) => void;
  timeSunday: string[];
  setTimeSunday: (time: string[]) => void;

  intervalMinutes: number;
  setIntervalMinutes: (minutes: number) => void;
}

const WEEKDAY_KEYS = [
  "monday",
  "tuesday",
  "wednesday",
  "thursday",
  "friday",
  "saturday",
  "sunday",
];

const WorkingScaleSelector = (props: WorkingScaleSelectorProps) => {
  const { t } = useTranslation();
  const {
    timeMonday, setTimeMonday,
    timeTuesday, setTimeTuesday,
    timeWednesday, setTimeWednesday,
    timeThursday, setTimeThursday,
    timeFriday, setTimeFriday,
    timeSaturday, setTimeSaturday,
    timeSunday, setTimeSunday,
    intervalMinutes,
    setIntervalMinutes
  } = props;

  const [selectedWeekDayIndex, setSelectedWeekDayIndex] = useState(0);

  const dynamicPossibleTimes = useMemo(() => {
    return generateTimeSlots(7, 22, intervalMinutes);
  }, [intervalMinutes]);

  const selectedWeekDayTimes = [
    timeMonday, timeTuesday, timeWednesday, timeThursday,
    timeFriday, timeSaturday, timeSunday,
  ];

  const setSelectedWeekDayTimes = [
    setTimeMonday, setTimeTuesday, setTimeWednesday, setTimeThursday,
    setTimeFriday, setTimeSaturday, setTimeSunday,
  ];

  const handleChangeSelectedWeekDayIndex = (_: any, newIndex: number) =>
      newIndex != null && setSelectedWeekDayIndex(newIndex);

  const handleIntervalChange = (event: SelectChangeEvent<number>) => {
    setIntervalMinutes(Number(event.target.value));
  };

  const handleChangeTimeAvailability = (dayTimeLabel: string) => {
    const temporaryDayTimeArray = [...selectedWeekDayTimes[selectedWeekDayIndex]];

    if (!temporaryDayTimeArray.includes(dayTimeLabel)) {
      temporaryDayTimeArray.push(dayTimeLabel);
    } else {
      const index = temporaryDayTimeArray.indexOf(dayTimeLabel);
      temporaryDayTimeArray.splice(index, 1);
    }

    temporaryDayTimeArray.sort((a, b) => {
      const [hA, mA] = a.split(":").map(Number);
      const [hB, mB] = b.split(":").map(Number);
      return (hA * 60 + mA) - (hB * 60 + mB);
    });

    setSelectedWeekDayTimes[selectedWeekDayIndex](temporaryDayTimeArray);
  };

  const currentDayLabel = t(`timeRange.days.${WEEKDAY_KEYS[selectedWeekDayIndex]}`);

  return (
    <Box sx={sharedStyles.verticalContent}>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', width: '100%', mb: 2 }}>
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>{t('workingScale.selector.intervalLabel')}</InputLabel>
            <Select
                value={intervalMinutes}
                label={t('workingScale.selector.intervalLabel')}
                onChange={handleIntervalChange}
            >
              <MenuItem value={60}>{t('workingScale.selector.intervals.1hour')}</MenuItem>
              <MenuItem value={30}>{t('workingScale.selector.intervals.30min')}</MenuItem>
              <MenuItem value={15}>{t('workingScale.selector.intervals.15min')}</MenuItem>
            </Select>
          </FormControl>
        </Box>

        <ToggleButtonGroup
            size="large"
            fullWidth
            value={selectedWeekDayIndex}
            onChange={handleChangeSelectedWeekDayIndex}
            exclusive
            color="primary"
        >
          {WEEKDAY_KEYS.map((key, index) => (
              <ToggleButton
                  key={key}
                  sx={{ textTransform: "none", paddingY: 1, paddingX: 1 }}
                  value={index}
              >
                <Typography variant="body2">
                  {t(`timeRange.days.${key}`).slice(0, 3)}
                </Typography>
              </ToggleButton>
          ))}
        </ToggleButtonGroup>

        <Typography variant="subtitle1" fontWeight="bold" sx={{ mt: 2 }}>
          {t('workingScale.selector.hoursFor', { day: currentDayLabel })}:
        </Typography>

        <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "repeat(3, 1fr)",
                sm: "repeat(4, 1fr)",
                md: "repeat(6, 1fr)",
              },
              gap: 1,
              width: "100%",
              maxHeight: "400px",
              overflowY: "auto",
              p: 1,
            }}
        >
          {dynamicPossibleTimes.length > 0 ? (
              dynamicPossibleTimes.map((dayLabel) => {
                const isTimeAvailable =
                    selectedWeekDayTimes[selectedWeekDayIndex].includes(dayLabel);

                return (
                    <Box
                        key={dayLabel}
                        onClick={() => handleChangeTimeAvailability(dayLabel)}
                        sx={{
                          cursor: "pointer",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          padding: "10px 0",
                          borderRadius: "8px",
                          border: `1px solid ${
                              isTimeAvailable ? validAvailabilityBorder : invalidAvailabilityBorder
                          }`,
                          backgroundColor: isTimeAvailable
                              ? validAvailability
                              : invalidAvailability,
                          transition: "all 0.2s ease-in-out",
                          "&:hover": { transform: "scale(1.02)" },
                          "&:active": { transform: "scale(0.98)" },
                        }}
                    >
                      <Typography
                          variant="body2"
                          sx={{ color: "rgba(0,0,0,0.5)", fontWeight: "bold" }}
                      >
                        {dayLabel}
                      </Typography>
                    </Box>
                );
              })
          ) : (
              <Box gridColumn="1 / -1" textAlign="center" py={3}>
                <Typography color="text.secondary">
                  {t('workingScale.selector.noTimesAvailable')}
                </Typography>
              </Box>
          )}
        </Box>

        <WorkingScaleCaption />
      </Box>
  );
};

export default WorkingScaleSelector;